/**
 * Plain-language text for the run panel: status, models and why a run waits.
 */

const STATUS_TEXT = {
  queued: 'Waiting to start',
  claimed: 'Starting',
  planning: 'Planning',
  awaiting_confirmation: 'Waiting for your go-ahead',
  fetching: 'Fetching data',
  analyzing: 'Analyzing',
  reviewing: 'Checking the findings',
  writing: 'Writing the report',
  publishing: 'Posting the results',
  cancelling: 'Cancelling…',
  completed: 'Done',
  completed_partial: 'Done (partial)',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

export function statusLabel(status, detail) {
  const base = STATUS_TEXT[String(status)] || String(status || '').replace(/_/g, ' ');
  const extra = detail && String(detail) !== base ? String(detail) : '';
  return extra && !/^Awaiting confirmation$/i.test(extra) ? `${base} — ${extra}` : base;
}

const MODEL_NAMES = {
  'deepseek-chat': 'DeepSeek',
  'kimi-k3': 'Kimi K3',
};

function modelName(id) {
  const key = String(id || '');
  if (MODEL_NAMES[key]) return MODEL_NAMES[key];
  if (/^claude/i.test(key)) return 'Claude';
  if (/^gpt/i.test(key)) return key.toUpperCase();
  return key;
}

/**
 * One plain line instead of per-role model ids, e.g.
 * "Planning and writing with Kimi K3 · analysis with DeepSeek".
 * @param {unknown} models
 */
export function plainModels(models) {
  let map = models;
  if (typeof map === 'string') {
    try { map = JSON.parse(map); } catch { map = null; }
  }
  if (!map || typeof map !== 'object' || Array.isArray(map)) return '';
  const lead = modelName(map.planner || map.writer);
  const analyst = modelName(map.analyst || map.source);
  if (!lead && !analyst) return '';
  if (!analyst || lead === analyst) return `Using ${lead || analyst}`;
  return `Planning and writing with ${lead} · analysis with ${analyst}`;
}

/** Why a run waits for confirmation (plan 2 §7.6). */
export function confirmReason(run) {
  if (run && run.mode === 'report') return 'Full reports wait for your go-ahead before fetching data.';
  let plan = run && run.plan;
  if (typeof plan === 'string') {
    try { plan = JSON.parse(plan); } catch { plan = null; }
  }
  const n = plan && Array.isArray(plan.sourceTasks) ? plan.sourceTasks.length : 0;
  return n
    ? `This plan fetches from ${n} sources, so it waits for your go-ahead.`
    : 'This plan waits for your go-ahead before fetching data.';
}
